'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const konyaProduct: Product = {
  slug: 'konya-bondex-sunger',
  name: 'Konya Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Konya bondex sünger; konut, ticari ve endüstriyel projelerde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Konya bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Konya içi projelere özel bir ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-5.webp',
  sku: 'BDX-KNY',
  mpn: 'BDX-KNY',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Konya bondex sünger nerelerde kullanılır?',
      answer:
        'Konut, ofis, atölye, depo, teknik oda ve makine hacimlerinde kullanılır. Konya’da hem merkez konut projelerinde hem sanayi tarafında tercih edilir.',
    },
    {
      question: 'Konya içinde teslimat süresi ne kadar?',
      answer:
        'Stok durumuna göre Konya merkez ve ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük metrajlı projelerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Hangi kalınlık öneriliyor?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek seviyede 30-40mm, zorlu teknik senaryolarda 50mm ve bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Konya'da keşif desteği var mı?",
      answer:
        'Evet. Projenin kullanım tipi, metrajı ve hedeflenen konfor seviyesine göre teknik keşif ve katman önerisi sağlanır.',
    },
    {
      question: 'Konya bondex sünger fiyatı ne kadar?',
      answer:
        "Konya bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat, kalınlık, metraj ve proje kurgusuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Mehmet Yıldırım',
      datePublished: '2026-04-01',
      ratingValue: 5,
      title: 'Selçuklu konutunda iyi sonuç',
      body:
        "Daire zemin uygulamasında kullandık. Üst kat kaynaklı darbe sesi gözle görülür şekilde azaldı.",
      verified: true,
    },
    {
      authorName: 'Ayça Demir',
      datePublished: '2026-04-07',
      ratingValue: 5,
      title: 'Meram ofis projesinde memnunuz',
      body:
        "Toplantı odalarında ses taşmasını azaltmak için uyguladık. Çalışma konforu ciddi şekilde arttı.",
      verified: true,
    },
    {
      authorName: 'Kerem Kaya',
      datePublished: '2026-04-12',
      ratingValue: 4,
      title: 'Karatay teknik hacimde işe yaradı',
      body:
        'Makine odasındaki titreşim kaynaklı rahatsızlık için kullandık. Beklentimize yakın bir iyileşme aldık.',
      verified: true,
    },
  ],
};

export function CityBondexKonya() {
  const url = toAbsoluteUrl('/konya-bondex-sunger');
  const title = 'Konya Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Konya bondex sünger, konut ve teknik projelerde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Konya bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (konya-bondex-sunger)');
  const displayPrice = konyaProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(konyaProduct.priceTRY)
    : null;

  const pageReviews =
    konyaProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Konya içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Konya merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik yönlendirme' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Konya Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(konyaProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Konya Bondex Sünger' },
          { '@type': 'Thing', name: 'Konya ses yalıtımı' },
          { '@type': 'Thing', name: 'teknik hacim izolasyonu' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: konyaProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(konyaProduct.imagePath), width: 1200, height: 750 }],
        description: konyaProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: konyaProduct.category,
        url,
        sku: konyaProduct.sku,
        mpn: konyaProduct.mpn,
        productID: konyaProduct.sku || konyaProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: konyaProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Konya' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(konyaProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(konyaProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: konyaProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: konyaProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Konya'da Bondex Sünger Uygulamalarında Doğru Model Nasıl Kurulur?</h2>
            <p className={styles.seoText}>
              Konya'da ses yalıtımı ihtiyacı, geniş coğrafya ve farklı yapı tipleri nedeniyle tek tip çözümlerle yönetilemez. Selçuklu ve
              Meram tarafında konut ve ofis ağırlığı öne çıkarken, Karatay ve sanayiye yakın hatlarda teknik hacim ve üretim etkili projeler
              daha sık görülür. Bu farklılık, ses şikayetlerinin kaynağını da değiştirir: konutta darbe ve komşu konuşma geçişi, teknik
              hacimlerde titreşim ve sürekli uğultu daha baskın hale gelir.
            </p>
            <p className={styles.seoText}>
              Konya bondex sünger yaklaşımı bu nedenle ürün adı odaklı değil senaryo odaklı ilerler. Rebonded süngerpan yapısı darbe ve
              titreşim kaynaklı enerjinin yüzeylerde taşınmasını azaltmaya yardımcı olur; ancak proje başarısı, doğru kalınlık seçimi ve
              uygulama disiplinine bağlıdır. Yüzey hazırlığı, birleşim sürekliliği, kenar dönüşü ve katman planı doğru yapılmadığında sonuç
              zayıflar.
            </p>
            <h3 className={styles.seoSubtitle}>Konut Tarafında En Sık Problemler</h3>
            <p className={styles.seoText}>
              Konya'da apartman ve site projelerinde kullanıcılar çoğunlukla üst kat adım sesi, duvar kaynaklı konuşma geçişi ve ortak alan
              gürültüsü nedeniyle çözüm arar. Bu tip projelerde hedef mutlak sessizlikten çok rahatsızlığı belirgin düşüren yaşam konforudur.
              Oda bazlı etaplama yaklaşımıyla hassas alanlara öncelik verildiğinde daha verimli sonuç alınır.
            </p>
            <h3 className={styles.seoSubtitle}>Ticari ve Teknik Alanlarda Yaklaşım</h3>
            <p className={styles.seoText}>
              Ofis projelerinde toplantı odası mahremiyeti ve çalışma alanı uğultusu yönetimi önceliklidir. Teknik hacimlerde ise ekipman
              titreşimi kaynak noktaya yakın çözülmelidir. Bu alanlarda doğru katman kurgusu için{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> ve ihtiyaç halinde{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> birlikte değerlendirilir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçiminde Denge</h3>
            <p className={styles.seoText}>
              20-30mm çoğu konut/ofis projesinde dengeli sonuç verirken, yüksek şikayetli teknik alanlarda 40-50mm çözümler gerekebilir.
              Gereksiz kalınlık maliyeti artırır; yetersiz kalınlık ise performansı düşürür. En sağlıklı yaklaşım, kritik bölgelere hedefli
              güçlendirme yapmaktır.
            </p>
            <h3 className={styles.seoSubtitle}>Konya'da Proje Planı Nasıl Okunmalı?</h3>
            <p className={styles.seoText}>
              Konya projelerinde ilk adım metraj değil, şikayetin teknik sınıflandırması olmalıdır. Örneğin kullanıcı "evde sürekli ses var"
              dediğinde bunun kaynağı çoğu zaman tek bir unsur değildir. Üst kat darbe etkisi, komşu daire konuşma sesi, ortak alan uğultusu,
              tesisat geçişi veya teknik oda kaynaklı titreşim aynı anda hissedilebilir. Bu noktada tek bir kalınlıkla tüm sorunu çözmeye
              çalışmak yerine, kaynakları ayrı ayrı ele almak gerekir.
            </p>
            <p className={styles.seoText}>
              Saha çalışmasında en çok zaman kaybettiren konu, sorunun yanlış tanımlanmasıdır. Darbe sorunu olan bir noktada yalnızca duvar
              odaklı çözüm üretmek veya konuşma geçişi olan bir noktada yalnızca zemin katmanını artırmak, maliyeti yükseltirken sonucu
              zayıflatabilir. Konya'da doğru planlama için hedef cümlesi net olmalıdır: "Gece adım sesini azaltmak", "toplantı odası ses
              taşmasını düşürmek", "makine odası etkisini komşu hacimde azaltmak" gibi net ve ölçülebilir hedefler daha sağlıklı sonuç verir.
            </p>
            <h3 className={styles.seoSubtitle}>Konut Projelerinde Katman Stratejisi</h3>
            <p className={styles.seoText}>
              Konya merkezde yeni konut projeleri artarken, eski apartman stokunda da yenileme talepleri devam ediyor. Yeni yapılarda katman
              planı erken aşamada yapılırsa daha verimli sonuç alınır. Eski yapılarda ise mevcut kaplama, döşeme durumu, tesisat geçişleri ve
              taşıyıcı detayları nedeniyle uygulama daha dikkatli planlanmalıdır. Bu fark dikkate alınmadan yapılan uygulamalar, teorik olarak
              doğru olsa bile pratikte hedeflenen konforu yakalayamayabilir.
            </p>
            <p className={styles.seoText}>
              Konutlarda kullanıcı memnuniyetini artıran model, tüm daireye aynı anda müdahale etmek yerine önceliklendirilmiş etaplamadır.
              Yatak odası, çocuk odası, çalışma odası gibi hassas hacimler önce ele alındığında kullanıcı tarafında kısa sürede fark hissedilir.
              Bu yaklaşım bütçenin daha kontrollü yönetilmesini sağlar. Ayrıca uygulama sürecinde yaşamın tamamen durmasını önleyerek proje
              deneyimini daha konforlu hale getirir.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Kurumsal Alanlarda Akustik Yönetim</h3>
            <p className={styles.seoText}>
              Konya ofis projelerinde en büyük problem, toplantı odası ve yönetici odalarında konuşma mahremiyetinin zayıf olmasıdır. Bunun
              yanında açık ofislerde sürekli konuşma uğultusu odaklanmayı düşürür. Bu iki ihtiyacın çözümü aynı değildir. Mahremiyet hedefinde
              geçiş noktaları (kapı çevresi, tavan birleşimi, priz ve tesisat boşlukları) kritik olurken, açık ofis konforunda genel akustik
              denge daha önemlidir.
            </p>
            <p className={styles.seoText}>
              Uygulamada sık yapılan hata, yalnızca tek yüzeye odaklanmaktır. Oysa ses çoğu zaman en zayıf noktadan geçer. Bu nedenle kurumsal
              projelerde sistem yaklaşımı gerekir: duvar, tavan, birleşim, kapı hattı ve teknik geçişler birlikte ele alınmalıdır. Bu planlama
              yapıldığında hem gizlilik hem çalışma konforu tarafında daha dengeli sonuç alınır.
            </p>
            <h3 className={styles.seoSubtitle}>Sanayi ve Teknik Hacimlerde Öncelik</h3>
            <p className={styles.seoText}>
              Konya'nın üretim odaklı bölgelerinde teknik oda uygulamaları konut projelerine göre daha farklı kriterlerle yürütülür. Makine,
              kompresör, fan ve pompa odalarında sesin yanında titreşim transferi de önemli bir problemdir. Bu nedenle çözüm, yalnızca yüzey
              kaplama değil, kaynak noktaya yakın kontrol ve taşıyıcıya geçişi kıran detaylarla birlikte planlanmalıdır.
            </p>
            <p className={styles.seoText}>
              Teknik hacim projelerinde bakım erişimi kritik bir parametredir. Uygulama sonrasında servis kapakları, bakım koridorları ve
              ekipman erişimi kapanırsa işletme tarafında yeni sorunlar oluşur. Doğru proje yaklaşımı, teknik performans ile bakım operasyonunu
              birlikte koruyan bir katman modelidir. Konya'da uzun ömürlü ve işletme dostu sonuç için bu yaklaşım vazgeçilmezdir.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Hataları ve Önleme Adımları</h3>
            <p className={styles.seoText}>
              En sık görülen hatalar; yetersiz yüzey temizliği, yanlış yapıştırıcı seçimi, ek yerlerinin açık kalması, kenar dönüşlerinin
              süreksiz bırakılması ve iş programı baskısıyla kalite kontrolün atlanmasıdır. Bu hataların çoğu malzemenin kendisinden değil,
              uygulama disiplininden kaynaklanır. Bu nedenle projede ara kontrol adımı mutlaka planlanmalıdır.
            </p>
            <p className={styles.seoText}>
              Ara kontrol modeli basittir: katman yerleşimi sonrası birleşim kontrolü, kritik noktaların gözden geçirilmesi, lokal düzeltme ve
              son onay. Bu adımların tamamı aynı gün içinde yapılabildiğinde tekrar iş oranı düşer. Özellikle metrajı yüksek Konya projelerinde
              bu disiplin zaman ve maliyet açısından ciddi avantaj sağlar.
            </p>
            <h3 className={styles.seoSubtitle}>Teslimat ve Termin Yönetimi</h3>
            <p className={styles.seoText}>
              Konya'da teslimat planı sadece kilometreye göre yapılmaz. Şantiye erişimi, bina giriş koşulları, kat taşıma düzeni, çalışma saati
              izinleri ve yükleme-boşaltma alanı birlikte değerlendirilmelidir. Büyük projelerde tek seferde toplu sevkiyat yerine etaplı
              sevkiyat modeli tercih edildiğinde sahadaki stok düzeni korunur, hasar riski azalır ve uygulama hızı artar.
            </p>
            <p className={styles.seoText}>
              Termin yönetiminde en kritik nokta, farklı ekiplerin aynı plana bağlı çalışmasıdır. Tesisat, kaplama, yalıtım ve ince iş ekipleri
              birbirinden kopuk ilerlerse iş uzar. Bu yüzden proje başında kısa ama net bir iş akışı planı yapılması, sonradan oluşacak
              gecikmeleri büyük ölçüde önler.
            </p>
            <h3 className={styles.seoSubtitle}>Maliyet-Performans Dengesini Koruma</h3>
            <p className={styles.seoText}>
              Konya projelerinde doğru yatırım, en pahalı çözümü almak değil; hedefe uygun çözümü doğru noktada uygulamaktır. Gereksiz kalınlık
              maliyeti artırırken, yetersiz kalınlık beklentiyi düşürür. En verimli yaklaşım, yüksek riskli alanlarda güçlendirme yapıp düşük
              riskli alanlarda optimize sistem kullanmaktır.
            </p>
            <p className={styles.seoText}>
              Bu denge sağlandığında kullanıcı tarafında hissedilen iyileşme artar ve yatırımın geri dönüşü daha görünür hale gelir. Bu nedenle
              karar aşamasında sadece birim fiyat değil, toplam proje etkisi değerlendirilmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Konya'ya Özel İçerik ve Doğru Yönlendirme</h3>
            <p className={styles.seoText}>
              Bu sayfadaki içerik, şehir adı değiştirilen standart bir metin değildir. Konya'nın konut, ofis ve teknik hacim dengesi; ilçe
              bazlı kullanım farkları; teslimat ve uygulama pratiği dikkate alınarak oluşturulmuştur. Amaç arama görünürlüğü için tekrarlı
              metin üretmek değil, kullanıcıya proje kararını netleştiren gerçek bilgi sunmaktır.
            </p>
            <p className={styles.seoText}>
              Projeniz için net öneri almak isterseniz kullanım tipi, metraj, şikayet tanımı ve hedef konfor seviyesini paylaşmanız yeterlidir.
              Bu bilgilerle birlikte en uygun kalınlık ve katman kurgusu daha hızlı ve güvenli biçimde oluşturulabilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler Özeti</h2>
            <p className={styles.seoText}>
              Yüksek yoğunluklu rebonded yapı, darbe ve titreşim etkisinin kontrolünde destek sağlar. Uygulama başarısı, birleşim detayları ve
              yüzey hazırlığıyla birlikte değerlendirilmelidir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Konya'da Kullanım Alanları</h2>
            <p className={styles.seoText}>
              Konut, ofis, teknik oda, atölye ve depo projelerinde kullanım görülür. Senaryoya göre zemin, duvar veya teknik hacim katmanında
              çözüm kurgulanır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Süreç Planı</h2>
            <p className={styles.seoText}>
              Konya içi sevkiyat proje erişimine göre planlanır. Büyük metrajlı uygulamalarda etaplı teslimat ve teknik kontrol adımlarıyla
              süreç daha stabil yönetilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Konya Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={konyaProduct.imagePath} alt="Konya bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Konya Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-KNY | Konya İçi Projeler
          </p>
          <p className={styles.meta}>
            Konya'da <strong>bondex sünger</strong> ihtiyacı olan konut, ofis ve teknik hacim projelerinde doğru kalınlık, hızlı tedarik ve
            proje bazlı teknik planlama ile ilerliyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Konya içi hızlı teslimat</span>
            <span className="chip">Konut ve teknik alan uyumlu</span>
          </div>

          <div className={styles.specGrid} aria-label="Konya için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {konyaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Konya Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={konyaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
